import * as React from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Skeleton,
  LinearProgress,
  Tooltip,
  IconButton,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TimerIcon from '@mui/icons-material/Timer';
import SpeedIcon from '@mui/icons-material/Speed';
import EuroIcon from '@mui/icons-material/Euro';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { withTranslation } from 'react-i18next';
import MetricCard from '../components/MetricCard';
import ExportButtons from '../components/ExportButtons';
import { useWorkTimeReport } from '../hooks/useReportsApi';
import { useUserStore } from '../../../store';
import { formatQuantity } from '../../../utils';

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '–';
  return new Intl.NumberFormat('fi-FI', {
    style: 'currency',
    currency: 'EUR',
  }).format(value);
};

function HelpTooltip({ title }) {
  return (
    <Tooltip title={title} enterTouchDelay={0} leaveTouchDelay={3000} arrow>
      <IconButton size="small" sx={{ ml: 0.5, p: 0.25 }} tabIndex={0}>
        <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
      </IconButton>
    </Tooltip>
  );
}

function WorkTimeSubTab({ t, dateParams }) {
  const { data, loading, error } = useWorkTimeReport(dateParams);
  // §11 presence/idle/recovery columns only apply to the live timer system.
  const timeClockEnabled = useUserStore((s) => s.currentOrganization?.time_clock_enabled) ?? false;

  if (error) {
    return <Alert severity="error">{t('reports.error')}: {error}</Alert>;
  }

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <ExportButtons reportType="employees" params={{ ...dateParams, sub_tab: 'worktime' }} />
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={AccessTimeIcon}
            label={t('reports.employees.wt.workTime')}
            value={data?.work_hours}
            loading={loading}
            format="hours"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={TimerIcon}
            label={t('reports.employees.wt.billableHours')}
            value={data?.billable_hours}
            loading={loading}
            format="hours"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={SpeedIcon}
            label={t('reports.employees.wt.utilizationRate')}
            value={data?.utilization_rate}
            loading={loading}
            format="percent"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={EuroIcon}
            label={t('reports.employees.wt.effectiveHourlyRate')}
            value={data?.effective_hourly_rate}
            loading={loading}
            format="currency"
          />
        </Grid>
      </Grid>

      {/* §11 presence-derived KPIs (live timer only) */}
      {timeClockEnabled && (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={AccessTimeIcon}
            label={t('reports.employees.wt.presence')}
            value={data?.presence_hours}
            loading={loading}
            format="hours"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={HourglassEmptyIcon}
            label={t('reports.employees.wt.breakHrs')}
            value={data?.break_hours}
            loading={loading}
            format="hours"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={SpeedIcon}
            label={t('reports.employees.wt.presenceUtilization')}
            value={data?.presence_utilization}
            loading={loading}
            format="percent"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={TrendingUpIcon}
            label={t('reports.employees.wt.recovery')}
            value={data?.recovery_rate}
            loading={loading}
            format="percent"
          />
        </Grid>
      </Grid>
      )}

      {/* Work Time Table */}
      <Paper elevation={0} sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {t('reports.employees.wt.title')}
        </Typography>
        {loading ? (
          <Skeleton variant="rectangular" height={300} />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('reports.employees.employee')}</TableCell>
                  <TableCell align="right">
                    {t('reports.employees.wt.workTime')}
                    <HelpTooltip title={t('reports.employees.wt.tooltipWorkTime')} />
                  </TableCell>
                  <TableCell align="right">
                    {t('reports.employees.wt.billable')}
                    <HelpTooltip title={t('reports.employees.wt.tooltipBillable')} />
                  </TableCell>
                  <TableCell align="right">
                    {t('reports.employees.wt.utilization')}
                    <HelpTooltip title={t('reports.employees.wt.tooltipUtilization')} />
                  </TableCell>
                  <TableCell align="right">
                    {t('reports.employees.wt.effectiveRate')}
                    <HelpTooltip title={t('reports.employees.wt.tooltipEffectiveRate')} />
                  </TableCell>
                  {timeClockEnabled && (
                    <>
                      <TableCell align="right">{t('reports.employees.wt.presence')}</TableCell>
                      <TableCell align="right">{t('reports.employees.wt.breakHrs')}</TableCell>
                      <TableCell align="right">
                        {t('reports.employees.wt.presenceUtilization')}
                        <HelpTooltip title={t('reports.employees.wt.tooltipPresenceUtilization')} />
                      </TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.employees || []).map((employee) => (
                  <TableRow key={employee.id}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 500 }}>{employee.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{employee.role}</Typography>
                    </TableCell>
                    <TableCell align="right" data-testid="wt-work-hours">
                      {employee.work_hours != null ? `${formatQuantity(employee.work_hours)} h` : '–'}
                    </TableCell>
                    <TableCell align="right">
                      {employee.billable_hours != null ? `${formatQuantity(employee.billable_hours)} h` : '–'}
                    </TableCell>
                    <TableCell align="right">
                      {employee.utilization_rate != null ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(employee.utilization_rate, 100)}
                            sx={{
                              width: 60,
                              height: 6,
                              borderRadius: 3,
                              bgcolor: 'grey.200',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: employee.utilization_rate >= 80 ? 'success.main' : 'warning.main',
                                borderRadius: 3,
                              },
                            }}
                          />
                          {employee.utilization_rate.toFixed(0)}%
                        </Box>
                      ) : '–'}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(employee.effective_hourly_rate)}
                    </TableCell>
                    {timeClockEnabled && (
                      <>
                        <TableCell align="right">
                          {employee.presence_hours != null ? `${formatQuantity(employee.presence_hours)} h` : '–'}
                        </TableCell>
                        <TableCell align="right">
                          {employee.break_hours != null ? `${formatQuantity(employee.break_hours)} h` : '–'}
                        </TableCell>
                        <TableCell align="right">
                          {employee.presence_utilization != null ? `${Number(employee.presence_utilization).toFixed(0)}%` : '–'}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))}
                {(!data?.employees || data.employees.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={timeClockEnabled ? 8 : 5} align="center" sx={{ color: 'text.secondary' }}>
                      {t('reports.noData')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}

export default withTranslation()(WorkTimeSubTab);
